import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScanHeart, Search, CalendarDays } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { inputCls, ghostBtnCls, primaryBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy, today } from "@/lib/format";

const reportAction = (b) => {
  if (b.report_released && !b.report_revoked) return { label: "View Report", tone: "ghost" };
  if (["verified", "signed_off"].includes(b.status)) return { label: "View / Sign", tone: "ghost" };
  if (["result_entered", "in_progress"].includes(b.status)) return { label: "Edit Report", tone: "primary" };
  return { label: "Add Report", tone: "primary" };
};

// mode: "today" | "search"
export default function DiagnosticCases({ mode = "today" }) {
  const { can } = useAuth();
  const [mods, setMods] = useState(null);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [day, setDay] = useState(today());
  const [f, setF] = useState({ from_date: "", to_date: "", q: "", status: "", has_due: false });

  useEffect(() => {
    api.get("/diagnostic/modalities")
      .then(({ data }) => { setMods(data.modalities); if (data.modalities[0]) setActive(data.modalities[0].key); })
      .catch((e) => setError(apiError(e)));
  }, []);

  const load = useCallback(async () => {
    if (!active) return;
    setItems(null);
    try {
      const params = { modality: active };
      if (mode === "today") { params.from_date = day; params.to_date = day; }
      else {
        Object.assign(params, {
          from_date: f.from_date || undefined, to_date: f.to_date || undefined,
          q: f.q || undefined, status: f.status || undefined, has_due: f.has_due || undefined,
        });
      }
      const { data } = await api.get("/diagnostic/cases", { params });
      setItems(data.items);
    } catch (e) { setError(apiError(e)); }
  }, [active, mode, day, f]);

  useEffect(() => { if (mode === "today") load(); /* eslint-disable-next-line */ }, [active, day, mode]);
  useEffect(() => { if (mode === "search" && active) load(); /* eslint-disable-next-line */ }, [active, mode]);

  if (!can("diagnostic.access")) return <NoAccess what="the diagnostic / scanning module" />;
  if (error && !mods) return <ErrorState message={error} />;
  if (!mods) return <Loading />;
  if (mods.length === 0) {
    return (
      <div data-testid="diagnostic-cases">
        <PageHeader overline="Diagnostic / Scanning" title="Imaging Cases" />
        <EmptyState title="No modality access" testid="diagnostic-no-modality"
          subtitle="Your role has no diagnostic modality permissions. Ask an administrator to enable USG, X-Ray, CT, MRI etc. under Staff & Permissions → Diagnostic / Scanning." />
      </div>
    );
  }

  const cols = ["Date", "Reg No", "Patient", "Age / Sex", "Referrer", "Investigation", "Status", "Due", "Actions"];
  const Row = (b) => {
    const act = reportAction(b);
    return (
      <tr key={b.bill_id} data-testid={`diag-case-${b.bill_id}`}>
        <Td className="text-slate-500 text-xs whitespace-nowrap">{dmy(b.created_at)}</Td>
        <Td className="font-mono text-slate-700">{b.reg_no}</Td>
        <Td className="font-medium text-slate-900">{b.patient_title} {b.patient_name}
          <span className="block text-xs text-slate-400">{b.patient_uhid || b.patient_phone}</span></Td>
        <Td className="text-slate-600 text-xs">{b.patient_age_display || "—"} / {b.patient_gender || "—"}</Td>
        <Td className="text-slate-600">{b.referrer_name || "—"}</Td>
        <Td className="text-slate-700 text-xs max-w-[220px]">{(b.investigations || []).join(", ") || "—"}</Td>
        <Td><StatusPill status={b.status} /></Td>
        <Td className={b.due > 0 ? "text-red-600 font-medium" : "text-slate-500"}>{b.due > 0 ? inr(b.due) : "—"}</Td>
        <Td>
          <Link to={`/app/lab/bills/${b.bill_id}`} data-testid={`diag-report-${b.bill_id}`}
            className={act.tone === "primary" ? primaryBtnCls : ghostBtnCls}>{act.label}</Link>
        </Td>
      </tr>
    );
  };

  return (
    <div data-testid="diagnostic-cases">
      <PageHeader overline="Diagnostic / Scanning" title={mode === "today" ? "Today's Cases" : "Search Cases"}
        subtitle="Imaging cases run on the unified order → billing → report → verify → sign → payment-gated release workflow." />

      <div className="flex flex-wrap gap-2 mb-5" data-testid="modality-tabs">
        {mods.map((m) => (
          <button key={m.key} onClick={() => setActive(m.key)} data-testid={`modality-${m.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
              active === m.key ? "bg-[#0F172A] text-white border-[#0F172A]"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-900"}`}>
            <ScanHeart className="h-3.5 w-3.5" strokeWidth={1.8} /> {m.label}
            <span className={`text-xs ${active === m.key ? "text-white/70" : "text-[#EA580C]"}`}>{m.cases}</span>
          </button>
        ))}
      </div>

      {mode === "today" ? (
        <div className="flex items-center gap-2 mb-4">
          <button className={ghostBtnCls} data-testid="diag-day-prev"
            onClick={() => setDay(new Date(new Date(day).getTime() - 86400000).toISOString().slice(0, 10))}>‹ Prev</button>
          <div className="relative">
            <CalendarDays className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="date" className={`${inputCls} pl-9`} value={day} onChange={(e) => setDay(e.target.value)} data-testid="diag-day-picker" />
          </div>
          <button className={ghostBtnCls} data-testid="diag-day-next"
            onClick={() => setDay(new Date(new Date(day).getTime() + 86400000).toISOString().slice(0, 10))}>Next ›</button>
          <button className={ghostBtnCls} onClick={() => setDay(today())}>Today</button>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="panel p-4 mb-5 grid grid-cols-2 md:grid-cols-5 gap-3">
          <input type="date" className={inputCls} value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} data-testid="diag-from" />
          <input type="date" className={inputCls} value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} data-testid="diag-to" />
          <input className={inputCls} placeholder="Patient / Reg / UHID" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="diag-q" />
          <select className={inputCls} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="diag-status">
            <option value="">Any status</option>
            {["new", "in_progress", "result_entered", "verified", "signed_off", "released"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <div className="flex gap-2">
            <button className={primaryBtnCls} data-testid="diag-search-btn"><Search className="h-4 w-4" /> Search</button>
            <button type="button" className={ghostBtnCls} onClick={() => setF({ from_date: "", to_date: "", q: "", status: "", has_due: false })}>Clear</button>
          </div>
        </form>
      )}

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="diag-cases-table" columns={cols} rows={items} renderRow={Row}
          empty={<EmptyState title="No cases" subtitle="No imaging cases match this modality and filter." testid="diag-cases-empty" />} />
      )}
    </div>
  );
}
